import React, { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useLocation } from 'react-router-dom';
import { v4 } from 'uuid';
import { TimeSignal } from 'graphql/types';
import { useUpdateTimeSignalsById } from 'modules/notes/hooks/useUpdateTimeSignalsById';
import { truncateMessage } from 'modules/notes/common';
import { useNotesToastContext } from 'modules/notes/NotesToastContextProvider';
import { routePathMap } from 'modules/common';
import { BackgroundTimerToastElement } from 'modules/common/components/BackgroundTimer/BackgroundTimerToastElement';
import { useNotesBackgroudTimerContext } from '../context/NotesBackgroudTimerContext';
import { extractSignalMetaData } from '../utils/helpers';
import { notesBackgroundStorage } from '../utils/NotesBackgroundStorage';
import { mapBackgroundNoteToTimeSignal } from '../utils/helpers';

export const useNotesBackgroudTimer = () => {
  const {
    initSignalContextValues,
    resetSignalContextValues,
    getCurrentInlineTimerDuration
  } = useNotesBackgroudTimerContext();

  const { setToastContextListValue } = useNotesToastContext();
  const { updateTimeSignalsById, loading, error } = useUpdateTimeSignalsById();
  const location = useLocation();

  const onStart = useCallback(
    ({ note }: { note: TimeSignal }) => {
      const signalMeta = extractSignalMetaData(note);
      const signalId = note.id;
      const timerStartTime = DateTime.now();
      const { set } = notesBackgroundStorage;
      initSignalContextValues({
        signalId: signalId,
        signalMeta: signalMeta,
        timerStartTime: timerStartTime
      });
      set({
        id: signalId,
        meta: signalMeta,
        timerStartTime: timerStartTime.toUnixInteger()
      });
    },
    [initSignalContextValues]
  );

  const onStop = useCallback(
    async (updateSignal = true): Promise<void> => {
      const { get, remove } = notesBackgroundStorage;
      const backgroundSignal = get();
      const calculatedDuration = getCurrentInlineTimerDuration();
      const toastMessage =
        location.pathname === routePathMap.notes
          ? 'toast.note.inlineTimer.myWork.success'
          : 'toast.note.inlineTimer.success';

      if (calculatedDuration && backgroundSignal?.id && updateSignal) {
        const input = mapBackgroundNoteToTimeSignal(
          backgroundSignal,
          calculatedDuration
        );
        await updateTimeSignalsById({
          updates: [input],
          awaitRefetchQueries: false,
          refetchQueries: ['timeSignalsCount']
        });
        if (!loading && !error) {
          resetSignalContextValues();
          remove();
          setToastContextListValue({
            id: `background-timer-success-${v4()}`,
            showSuccess: true,
            message: toastMessage,
            messageElement: (
              <BackgroundTimerToastElement
                message={toastMessage}
                description={
                  backgroundSignal?.meta?.description
                    ? truncateMessage(backgroundSignal?.meta?.description)
                    : ''
                }
              />
            )
          });
        } else if (error) {
          setToastContextListValue({
            id: `background-timer-failure-${v4()}`,
            showError: true,
            message: 'toast.note.inlineTimer.failure'
          });
        }
      } else {
        resetSignalContextValues();
        remove();
      }
    },
    [
      loading,
      error,
      location.pathname,
      updateTimeSignalsById,
      resetSignalContextValues,
      getCurrentInlineTimerDuration,
      setToastContextListValue
    ]
  );

  return useMemo(
    () => ({
      onStart,
      onStop
    }),
    [onStart, onStop]
  );
};
